// ═══════════════════════════════════════════════════════════════
// Impact — Home (Individual) Assessment Form
// Create a new Individual Assistance damage assessment
// Route: /impact/new/home
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Button, Group, Stack, SimpleGrid, Select,
  TextInput, Textarea, Stepper, Divider, Switch,
} from '../../components/gab/GabComponents';
import { IconSend, IconArrowLeft, IconArrowRight, IconHome, IconBackhoe, IconSparkles, IconSearch, IconUsers, IconX } from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import type { Assessment } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { toast } from './lib/toast';
import DamageCategorySelector from './components/DamageCategorySelector';
import DamageTypeSelector from './components/DamageTypeSelector';
import LocationInput from './components/LocationInput';
import type { LocationValue } from './components/LocationInput';
import PhotoCapture from './components/PhotoCapture';
import MapPicker from './components/MapPicker';
import { reverseGeocode, getCountyFromCoords } from './lib/gis';
import type { PhotoMetadata } from './lib/exif';
import { WORKER_URL } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

const RESIDENCE_TYPES = [
  { value: 'primary', label: 'Primary Residence' },
  { value: 'secondary', label: 'Secondary / Vacation' },
  { value: 'rental', label: 'Rental Property' },
  { value: 'mobile', label: 'Mobile Home' },
  { value: 'other', label: 'Other' },
];

const OWNERSHIP_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'renter', label: 'Renter' },
  { value: 'unknown', label: 'Unknown' },
];

const DWELLING_TYPES = [
  { value: 'single-family', label: 'Single Family' },
  { value: 'multi-family', label: 'Multi-Family' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condo/Townhouse' },
  { value: 'manufactured', label: 'Manufactured/Mobile' },
  { value: 'other', label: 'Other' },
];

interface FormState {
  location: LocationValue;
  damageCategory: string;
  severity: number | null;
  damageTypes: string[];
  description: string;
  residenceType: string;
  ownership: string;
  dwellingType: string;
  waterLineHeight: string;
  habitable: boolean | null;
  insured: boolean | null;
  occupants: string;
  photos: File[];
  residentPersonId: number | null;
  residentPersonName: string;
}

const INITIAL_FORM: FormState = {
  location: {},
  damageCategory: '',
  severity: null,
  damageTypes: [],
  description: '',
  residenceType: '',
  ownership: '',
  dwellingType: '',
  waterLineHeight: '',
  habitable: null,
  insured: null,
  occupants: '',
  photos: [],
  residentPersonId: null,
  residentPersonName: '',
};

function resizeImageToBase64(file: File, maxWidth = 1200, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        if (w > maxWidth) {
          h = Math.round((h * maxWidth) / w);
          w = maxWidth;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No canvas context')); return; }
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        // Strip data:image/jpeg;base64, prefix — store just the base64 part
        const base64 = dataUrl.split(',')[1] || dataUrl;
        resolve(base64);
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

export default function HomeAssessment() {
  const navigate = useNavigate();
  const { createAssessment, uploadPhoto } = useImpactStore();
  const { createRequest } = useDebrisOpsStore();
  const { user, activeIncident } = useAuthStore();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [tagForDebris, setTagForDebris] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Resident / property owner directory search
  const [residentQuery, setResidentQuery] = useState('');
  const [residentResults, setResidentResults] = useState<Array<{ id: number; firstName: string; lastName: string; email: string; phone: string }>>([]);
  const [residentSearching, setResidentSearching] = useState(false);
  const residentDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleResidentSearch = (q: string) => {
    setResidentQuery(q);
    if (residentDebounce.current) clearTimeout(residentDebounce.current);
    if (!q.trim()) { setResidentResults([]); return; }
    residentDebounce.current = setTimeout(async () => {
      if (!TABLES.people) return;
      setResidentSearching(true);
      try {
        const FP = FIELDS.people;
        const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
          search: q.trim(), limit: 8,
        });
        const lower = q.toLowerCase();
        setResidentResults(res.records
          .map(r => ({
            id: r.record_id as number,
            firstName: String(r[FP.firstName] ?? ''),
            lastName: String(r[FP.lastName] ?? ''),
            email: String(r[FP.email] ?? ''),
            phone: String(r[FP.phone] ?? ''),
          }))
          .filter(p => {
            const name = `${p.firstName} ${p.lastName}`.toLowerCase();
            return name.includes(lower) || p.email.toLowerCase().includes(lower);
          })
        );
      } catch { /* */ }
      setResidentSearching(false);
    }, 300);
  };

  // Guard: require active incident
  if (!activeIncident) {
    return (
      <Stack align="center" py="xl" gap="md">
        <IconHome size={48} color="#939598" />
        <Text size="lg" c="dimmed">No active incident selected</Text>
        <Text size="sm" c="dimmed">Please select an incident from the dashboard before creating an assessment.</Text>
        <Button variant="subtle" onClick={() => navigate('/impact/assessments')}>
          Back to Assessments
        </Button>
      </Stack>
    );
  }

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleLocationChange = (loc: LocationValue) => {
    updateForm('location', loc);
  };

  const handleMapChange = (pos: { lat: number; lng: number }) => {
    // Update coordinates, then reverse geocode for address/county
    const newLoc = { ...form.location, lat: pos.lat, lng: pos.lng };
    updateForm('location', newLoc);

    // Background geocode
    Promise.all([
      reverseGeocode(pos.lat, pos.lng),
      getCountyFromCoords(pos.lat, pos.lng),
    ]).then(([address, county]) => {
      setForm(prev => ({
        ...prev,
        location: {
          ...prev.location,
          lat: pos.lat,
          lng: pos.lng,
          address: address?.address || prev.location.address || '',
          county: county?.county || address?.county || prev.location.county || '',
          state: county?.state || address?.state || prev.location.state || '',
          fipsCode: county?.fipsCode || prev.location.fipsCode || '',
        },
      }));
    }).catch(() => {});
  };

  const handleGpsExtracted = (gps: { lat: number; lng: number }) => {
    // Auto-fill location from EXIF GPS
    const newLoc = { ...form.location, lat: gps.lat, lng: gps.lng };
    updateForm('location', newLoc);
    toast.success('Location extracted from photo EXIF data.');

    Promise.all([
      reverseGeocode(gps.lat, gps.lng),
      getCountyFromCoords(gps.lat, gps.lng),
    ]).then(([address, county]) => {
      setForm(prev => ({
        ...prev,
        location: {
          ...prev.location,
          lat: gps.lat,
          lng: gps.lng,
          address: address?.address || prev.location.address || '',
          county: county?.county || address?.county || prev.location.county || '',
          state: county?.state || address?.state || prev.location.state || '',
          fipsCode: county?.fipsCode || prev.location.fipsCode || '',
        },
      }));
    }).catch(() => {});
  };

  const handleExifMetadata = (_metadata: PhotoMetadata) => {
    // GPS already handled via onGpsExtracted callback
  };

  // ── AI Photo Analysis ──
  const handleAiDescribe = async () => {
    if (form.photos.length === 0) { toast.warning('Add photos first.'); return; }
    setAiLoading(true);
    try {
      const images = await Promise.all(
        form.photos.slice(0, 5).map(async (file: File) => {
          const b64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const img = new Image();
              img.onload = () => {
                let { width, height } = img;
                const max = 1024;
                if (width > max || height > max) { const s = max / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
                const c = document.createElement('canvas'); c.width = width; c.height = height;
                c.getContext('2d')!.drawImage(img, 0, 0, width, height);
                resolve(c.toDataURL('image/jpeg', 0.8).split(',')[1]);
              };
              img.onerror = reject;
              img.src = reader.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          return { type: 'image' as const, source: { type: 'base64' as const, media_type: 'image/jpeg' as const, data: b64 } };
        })
      );
      const n = images.length;
      const res = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{ role: 'user', content: [...images, { type: 'text', text: `I have uploaded ${n} photo${n > 1 ? 's' : ''} from a residential damage assessment. Describe the damage visible across ALL ${n} photo${n > 1 ? 's' : ''}, noting what each one shows if they capture different angles or areas. Be concise but thorough (${n > 1 ? '3-5' : '2-3'} sentences). Focus on: type of damage, severity, and structural impact. Do not use emojis.` }] }],
        }),
      });
      const result = await res.json();
      if (result.content?.[0]?.text) {
        updateForm('description', result.content[0].text);
        toast.success('AI description generated from photos.');
      } else { toast.error('AI could not generate a description.'); }
    } catch { toast.error('Failed to generate AI description.'); }
    finally { setAiLoading(false); }
  };

  const handleSubmit = async () => {
    // Validate
    if (!form.damageCategory) {
      toast.error('Please select a damage category');
      return;
    }

    setSubmitting(true);
    try {
      // Serialize all extras (including residentPersonId) into the notes JSON field
      const extras: Record<string, unknown> = { method: 'form' };
      if (form.damageTypes.length) extras.damageTypes = form.damageTypes;
      if (form.residenceType) extras.residenceType = form.residenceType;
      if (form.ownership) extras.ownership = form.ownership;
      if (form.dwellingType) extras.dwellingType = form.dwellingType;
      if (form.waterLineHeight) extras.waterLineHeight = form.waterLineHeight;
      if (form.habitable !== null) extras.habitable = form.habitable;
      if (form.insured !== null) extras.insured = form.insured;
      if (form.occupants) extras.occupants = form.occupants;
      if (form.severity !== null) extras.severity = form.severity;
      if (form.location.fipsCode) extras.fipsCode = form.location.fipsCode;
      if (form.location.state) extras.stateCode = form.location.state;
      if (form.residentPersonId) extras.residentPersonId = String(form.residentPersonId);
      if (form.residentPersonName) extras.residentPersonName = form.residentPersonName;

      const assessmentData: Partial<Assessment> = {
        incidentId: String(activeIncident.id),
        assessmentType: 'individual',
        damageCategory: form.damageCategory,
        address: form.location.address || '',
        county: form.location.county || '',
        state: form.location.state || '',
        latitude: form.location.lat ?? null,
        longitude: form.location.lng ?? null,
        description: form.description || '',
        occupancyType: form.residenceType,
        structureType: form.dwellingType,
        floodDepth: form.waterLineHeight ? Number(form.waterLineHeight) : undefined,
        assessorName: user?.fullName || '',
        assessorEmail: user?.email || '',
        notes: JSON.stringify(extras),
      };

      const assessmentId = await createAssessment(assessmentData);

      // Upload photos
      if (form.photos.length > 0) {
        for (let i = 0; i < form.photos.length; i++) {
          try {
            const base64 = await resizeImageToBase64(form.photos[i]);
            await uploadPhoto(assessmentId, base64, i);
          } catch (err) {
            console.error(`Photo ${i} upload failed:`, err);
          }
        }
      }

      // Tag for debris removal if checked
      if (tagForDebris) {
        try {
          await createRequest({
            incidentId: String(activeIncident.id),
            assessmentId: String(assessmentId),
            address: form.location.address || '',
            latitude: form.location.lat ? String(form.location.lat) : '',
            longitude: form.location.lng ? String(form.location.lng) : '',
            damageCategory: form.damageCategory || '',
            damageTypes: JSON.stringify(form.damageTypes || []),
            description: form.description || '',
            status: 'pending',
            taggedBy: user?.email ?? '',
            taggedAt: new Date().toISOString(),
          });
        } catch { /* non-blocking */ }
      }

      toast.success(tagForDebris ? 'Assessment created & tagged for debris removal!' : 'Assessment created successfully');
      navigate('/impact/assessments');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = (currentStep: number) => {
    switch (currentStep) {
      case 0: return true; // location is optional but encouraged
      case 1: return !!form.damageCategory;
      case 2: return true; // residence details optional
      default: return true;
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/impact/assessments')}
            size="sm"
          >
            Back
          </Button>
          <Title order={3} c="black">New Home Assessment</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Incident: {activeIncident.name}
        </Text>
      </Group>

      <Stepper active={step} onStepClick={setStep} size="sm" mb="lg">
        <Stepper.Step label="Location" description="Where">
          <Paper p="lg" withBorder radius="sm">
            <Stack gap="md">
              <LocationInput
                value={form.location}
                onChange={handleLocationChange}
              />

              <Divider label="Or click on map" labelPosition="center" />

              <MapPicker
                value={form.location.lat != null && form.location.lng != null
                  ? { lat: form.location.lat, lng: form.location.lng }
                  : null}
                onChange={handleMapChange}
                height={300}
              />
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Damage" description="What happened">
          <Paper p="lg" withBorder radius="sm">
            <Stack gap="md">
              <DamageCategorySelector
                value={form.damageCategory}
                onChange={(val) => updateForm('damageCategory', val)}
              />

              <DamageTypeSelector
                value={form.damageTypes}
                onChange={(val) => updateForm('damageTypes', val)}
                mode="ia"
              />

              <Group justify="space-between" align="flex-end">
                <Textarea
                  label="Description"
                  placeholder="Describe the damage observed..."
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  minRows={3}
                  autosize
                  style={{ flex: 1 }}
                />
              </Group>
              {form.photos.length > 0 && (
                <Button variant="light" size="xs" leftSection={<IconSparkles size={14} />}
                  onClick={handleAiDescribe} loading={aiLoading}>
                  AI Describe from Photos
                </Button>
              )}
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Residence" description="Details">
          <Paper p="lg" withBorder radius="sm">
            <Stack gap="md">

              {/* Resident / Property Owner — directory person link */}
              {TABLES.people && (
                <div>
                  <Text size="sm" fw={500} mb={4}>Resident / Property Owner</Text>
                  <Text size="xs" c="dimmed" mb="xs">
                    Link to an existing person in the directory to avoid duplicate records.
                  </Text>
                  {form.residentPersonId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 14 }}>
                      <IconUsers size={13} color="#16a34a" />
                      <span style={{ flex: 1, color: '#166534', fontWeight: 600 }}>{form.residentPersonName}</span>
                      <button onClick={() => { updateForm('residentPersonId', null); updateForm('residentPersonName', ''); setResidentQuery(''); setResidentResults([]); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}><IconX size={12} /></button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <TextInput
                        placeholder="Search People Directory by name or email..."
                        size="sm"
                        value={residentQuery}
                        onChange={e => handleResidentSearch(e.target.value)}
                        leftSection={<IconSearch size={14} />}
                      />
                      {(residentSearching || residentResults.length > 0 || (residentQuery && !residentSearching)) && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', zIndex: 100, marginTop: 2 }}>
                          {residentSearching && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>Searching…</div>}
                          {!residentSearching && residentResults.length === 0 && residentQuery && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>No matches — this may be a new person not yet in the directory</div>}
                          {residentResults.map(p => (
                            <div key={p.id}
                              onClick={() => {
                                updateForm('residentPersonId', p.id);
                                updateForm('residentPersonName', `${p.firstName} ${p.lastName}`);
                                setResidentQuery('');
                                setResidentResults([]);
                              }}
                              style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}>
                              <div style={{ fontWeight: 600 }}>{p.firstName} {p.lastName}</div>
                              <div style={{ color: '#6b7280' }}>{[p.email, p.phone].filter(Boolean).join(' · ')}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Select
                  label="Residence Type"
                  placeholder="Select type"
                  data={RESIDENCE_TYPES}
                  value={form.residenceType || null}
                  onChange={(v) => updateForm('residenceType', v || '')}
                  clearable
                />
                <Select
                  label="Ownership"
                  placeholder="Owner or renter"
                  data={OWNERSHIP_OPTIONS}
                  value={form.ownership || null}
                  onChange={(v) => updateForm('ownership', v || '')}
                  clearable
                />
                <Select
                  label="Dwelling Type"
                  placeholder="Select dwelling type"
                  data={DWELLING_TYPES}
                  value={form.dwellingType || null}
                  onChange={(v) => updateForm('dwellingType', v || '')}
                  clearable
                />
                <TextInput
                  label="Occupants"
                  placeholder="Number of occupants"
                  value={form.occupants}
                  onChange={(e) => updateForm('occupants', e.target.value)}
                  type="number"
                  min={0}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <div>
                  <Text size="sm" fw={500} mb={4}>Habitable?</Text>
                  <Group gap="sm">
                    <Button
                      size="sm"
                      variant={form.habitable === true ? 'filled' : 'outline'}
                      color="var(--color-success)"
                      onClick={() => updateForm('habitable', true)}
                    >
                      Yes
                    </Button>
                    <Button
                      size="sm"
                      variant={form.habitable === false ? 'filled' : 'outline'}
                      color="var(--color-error)"
                      onClick={() => updateForm('habitable', false)}
                    >
                      No
                    </Button>
                    {form.habitable != null && (
                      <Button
                        size="sm"
                        variant="subtle"
                        color="#939598"
                        onClick={() => updateForm('habitable', null)}
                      >
                        Clear
                      </Button>
                    )}
                  </Group>
                </div>

                <div>
                  <Text size="sm" fw={500} mb={4}>Insured?</Text>
                  <Group gap="sm">
                    <Button
                      size="sm"
                      variant={form.insured === true ? 'filled' : 'outline'}
                      color="var(--color-success)"
                      onClick={() => updateForm('insured', true)}
                    >
                      Yes
                    </Button>
                    <Button
                      size="sm"
                      variant={form.insured === false ? 'filled' : 'outline'}
                      color="var(--color-error)"
                      onClick={() => updateForm('insured', false)}
                    >
                      No
                    </Button>
                    {form.insured != null && (
                      <Button
                        size="sm"
                        variant="subtle"
                        color="#939598"
                        onClick={() => updateForm('insured', null)}
                      >
                        Clear
                      </Button>
                    )}
                  </Group>
                </div>
              </SimpleGrid>

              <TextInput
                label="Water Line Height (inches)"
                placeholder="Height of water line inside structure"
                value={form.waterLineHeight}
                onChange={(e) => updateForm('waterLineHeight', e.target.value)}
                type="number"
                min={0}
              />
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Photos" description="Evidence">
          <Paper p="lg" withBorder radius="sm">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Take or upload photos of the damage. GPS coordinates will be automatically extracted from photo metadata when available.
              </Text>
              <PhotoCapture
                photos={form.photos}
                onChange={(photos) => updateForm('photos', photos)}
                onGpsExtracted={handleGpsExtracted}
                onExifMetadata={handleExifMetadata}
                maxPhotos={10}
              />
              {form.photos.length > 0 && (
                <Button variant="light" size="sm" leftSection={<IconSparkles size={14} />}
                  onClick={handleAiDescribe} loading={aiLoading}>
                  AI Describe ({form.photos.length} photo{form.photos.length > 1 ? 's' : ''})
                </Button>
              )}
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Completed>
          <Paper p="lg" withBorder radius="sm">
            <Stack gap="md">
              <Title order={4}>Review Assessment</Title>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <div>
                  <Text size="xs" c="dimmed">Location</Text>
                  <Text size="sm">{form.location.address || 'Not specified'}</Text>
                  {form.location.county && <Text size="xs" c="dimmed">{form.location.county}{form.location.state ? `, ${form.location.state}` : ''}</Text>}
                </div>
                <div>
                  <Text size="xs" c="dimmed">Damage Category</Text>
                  <Text size="sm" fw={500} tt="capitalize">{form.damageCategory || 'Not selected'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Damage Types</Text>
                  <Text size="sm">{form.damageTypes.length > 0 ? form.damageTypes.join(', ') : 'None selected'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Residence</Text>
                  <Text size="sm">{form.residenceType || 'Not specified'} / {form.ownership || 'N/A'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Photos</Text>
                  <Text size="sm">{form.photos.length} photo{form.photos.length !== 1 ? 's' : ''}</Text>
                </div>
              </SimpleGrid>

              {form.description && (
                <div>
                  <Text size="xs" c="dimmed">Description</Text>
                  <Text size="sm">{form.description}</Text>
                </div>
              )}

              <Divider />

              <Group justify="space-between">
                <Switch
                  label="Tag for Debris Removal"
                  checked={tagForDebris}
                  onChange={e => setTagForDebris(e.currentTarget.checked)}
                  color="#F86A0B"
                  thumbIcon={tagForDebris ? <IconBackhoe size={12} /> : undefined}
                />
                <Group gap="sm">
                  <Button
                    variant="subtle"
                    onClick={() => setStep(0)}
                  >
                    Edit
                  </Button>
                  <Button
                    leftSection={<IconSend size={14} />}
                    onClick={handleSubmit}
                    loading={submitting}
                    size="md"
                  >
                    Submit Assessment
                  </Button>
                </Group>
              </Group>
            </Stack>
          </Paper>
        </Stepper.Completed>
      </Stepper>

      {/* Navigation Buttons (below stepper, not on completed step) */}
      {step < 4 && (
        <Group justify="space-between" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
          >
            Previous
          </Button>
          <Button
            rightSection={<IconArrowRight size={14} />}
            onClick={() => setStep(step + 1)}
            disabled={!canProceed(step)}
          >
            {step === 3 ? 'Review' : 'Next'}
          </Button>
        </Group>
      )}
    </div>
  );
}
